import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';

import { ChannelAdminsState } from './types';
import { GetAdmins } from '@/connection/admins/get-admins';
import { BLAdmin } from '@/types/profile.types';
import UnlockEmail from '@/connection/admins/unlock-email';
import AddAdmin from '@/connection/admins/add-admin';
import RemoveAdmin from '@/connection/admins/remove-admin';
import { EPermissions } from '@/types/permissions.types';
import { RootState } from '@/store/main';

export const reducers = (create: ReducerCreators<ChannelAdminsState>) => ({
  getAdmins: create.asyncThunk(
    async (
      {
        channel,
        includeLockedEmails,
        roles,
        bypassIfExists,
      }: {
        channel: number;
        includeLockedEmails?: boolean;
        roles?: EPermissions[];
        bypassIfExists?: boolean;
      },
      { getState }
    ) => {
      if (bypassIfExists) {
        const state = getState() as RootState;
        const channelAdminsState = state.channelAdmins as ChannelAdminsState;
        if (channelAdminsState?.admins?.length) {
          return {
            admins: channelAdminsState.admins,
            temporarilyLockedEmails: channelAdminsState.temporarilyLockedEmails,
          };
        }
      }
      const res = await GetAdmins({ channel, includeLockedEmails, roles });

      if (Array.isArray(res)) {
        return { admins: res };
      } else {
        return { admins: res.admins, temporarilyLockedEmails: res.temporarilyLockedEmails };
      }
    },
    {
      fulfilled: (state, action) => {
        state.admins = action.payload.admins;
        if (action.payload.temporarilyLockedEmails) {
          state.temporarilyLockedEmails = action.payload.temporarilyLockedEmails;
        }
      },
    }
  ),
  getChannelProducersAndPresenters: create.asyncThunk(
    async (_, { rejectWithValue, getState }) => {
      const state = getState() as RootState;
      const channel = state.user.user?.active_channel;
      if (!channel) {
        throw rejectWithValue('No active channel');
      }
      const currentMembers = state.channelAdmins?.activeChannelProducersAndPresenters as BLAdmin[] | null;
      if (currentMembers) {
        return { activeChannelProducersAndPresenters: currentMembers };
      }
      const res = await GetAdmins({
        channel,
        includeLockedEmails: false,
        roles: [EPermissions.Producer, EPermissions.Presenter],
      });

      if (Array.isArray(res)) {
        return { activeChannelProducersAndPresenters: res };
      } else {
        return { activeChannelProducersAndPresenters: res.admins };
      }
    },
    {
      fulfilled: (state, action) => {
        state.activeChannelProducersAndPresenters = action.payload.activeChannelProducersAndPresenters;
      },
    }
  ),
  addAdmin: create.asyncThunk(
    async ({ email, permissions, isSSO }: { email: string; permissions: string[]; isSSO: boolean }) => {
      const newAdmin = await AddAdmin(email, permissions, isSSO);
      return newAdmin;
    },
    {
      fulfilled: (state, action) => {
        state.admins = [...state.admins, action.payload];
      },
    }
  ),
  removeAdmin: create.asyncThunk(
    async ({ adminId, channel }: { adminId: number; channel: number }) => {
      const res = await RemoveAdmin(adminId, channel);
      return res;
    },
    {
      fulfilled: (state, action) => {
        state.admins = state.admins.filter((admin: BLAdmin) => admin.id !== action.payload.id);
      },
    }
  ),
  unlockEmail: create.asyncThunk(
    async ({ email }: { email: string }) => {
      const res = await UnlockEmail(email);
      return res;
    },
    {
      pending: (state) => {
        state.unlockingEmail = true;
      },
      fulfilled: (state, action) => {
        delete state.temporarilyLockedEmails[action.payload.unlocked_email];
      },
      settled: (state) => {
        state.unlockingEmail = false;
      },
    }
  ),
  setWorkingPermissions: create.reducer((state, action: PayloadAction<string[] | null>) => {
    state.workingPermissions = action.payload;
  }),
});
